(() => {
  const stateKey = 'dscCollectionLoadMore';
  const cacheKey = 'dsc:collection-load-more:v1';

  class CollectionLoadMore extends HTMLElement {
    connectedCallback() {
      this.life?.abort();
      this.request?.abort();

      this.grid = document.getElementById(
        'product-list-' + this.dataset.sectionId
      );

      this.fallback = this.closest(
        '[data-collection-pagination]'
      )?.querySelector('[data-load-more-fallback]');

      if (!this.grid || !this.fallback) return;

      this.life = new AbortController();
      this.invalidated = false;
      this.ready = false;
      this.cachedPage = null;
      this.cachedHTML = null;
      this.cachePendingPage = null;
      this.cachePendingHTML = null;
      this.cacheWriteId = Symbol();

      this.originalLabel = this.querySelector(
        '[data-load-more-action] a'
      )?.textContent.trim() || '';

      const saved = history.state?.[stateKey];

      this.state = saved?.context === this.context() ? saved : {
        id: crypto.randomUUID(),
        context: this.context(),
        firstPage: Number(this.dataset.page),
        lastPage: Number(this.dataset.page)
      };

      const options = { signal: this.life.signal };

      this.addEventListener('click', event => {
        const link = event.target.closest(
          '[data-load-more-action] a'
        );

        if (
          !link ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) return;

        event.preventDefault();

        if (!this.busy && !this.invalidated) {
          this.loadNext(event.detail === 0);
        }
      }, options);

      document.addEventListener('facet:update', event => {
        if (
          event.detail.url.searchParams.get('section_id') !==
          this.dataset.sectionId
        ) return;

        this.invalidated = true;
        this.request?.abort();
      }, options);

      window.addEventListener('pagehide', () => {
        this.save();
        this.request?.abort();
      }, options);

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.save();
        }
      }, options);

      window.addEventListener('pageshow', event => {
        if (event.persisted) this.setBusy(false);
      }, options);

      this.start(saved).catch(() => this.failRestore());
    }

    disconnectedCallback() {
      this.life?.abort();
      this.request?.abort();
    }

    context() {
      const url = new URL(location.href);

      url.hash = '';
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      url.searchParams.sort();

      return url.toString() + '|' +
        document.documentElement.lang + '|' +
        (window.Shopify?.currency?.active || '') + '|' +
        this.dataset.sectionId;
    }

    pageUrl(page) {
      const url = new URL(location.href);

      url.hash = '';
      url.searchParams.delete('section_id');
      url.searchParams.set('page', page);

      return url;
    }

    get cards() {
      return Array.from(this.grid.children).filter(
        node => node.matches('product-card')
      );
    }

    async start(saved) {
      this.setBusy(true);

      const previousMinHeight = this.grid.style.minHeight;
      let reservedHeight = false;

      try {
        if (saved?.context === this.context()) {
          if (
            saved.viewportWidth === window.innerWidth &&
            Number.isFinite(saved.gridHeight) &&
            saved.gridHeight > 0
          ) {
            this.grid.style.minHeight = saved.gridHeight + 'px';
            reservedHeight = true;
          }

          const cached = await this.readCache();

          if (!this.isConnected || this.invalidated) return;

          if (
            cached?.id === saved.id &&
            cached.lastPage === saved.lastPage &&
            (cached.snapshotId ?? null) === (saved.snapshotId ?? null) &&
            cached.total === this.dataset.total &&
            typeof cached.html === 'string' &&
            typeof cached.fallback === 'string' &&
            Number(cached.data?.page) === saved.lastPage &&
            Date.now() - cached.time < 600000
          ) {
            const template = document.createElement('template');

            template.innerHTML = cached.html;
            this.prepare(template.content);
            this.grid.replaceChildren(template.content);

            Object.assign(this.dataset, cached.data);
            this.fallback.innerHTML = cached.fallback;
            this.cachedPage = cached.lastPage;
            this.cachedHTML = cached.html;
          } else if (saved.lastPage > saved.firstPage) {
            const fragment = document.createDocumentFragment();

            this.request = new AbortController();

            let last;

            for (
              let page = saved.firstPage;
              page <= saved.lastPage;
              page++
            ) {
              last = await this.fetchPage(page, this.request.signal);
              this.prepare(last.grid);
              fragment.append(...last.grid.childNodes);
            }

            if (!this.isConnected || this.invalidated) return;

            this.grid.replaceChildren(fragment);
            this.applyPage(last);
          }

          await this.restoreScroll(saved);
        }
      } finally {
        if (reservedHeight) {
          this.grid.style.minHeight = previousMinHeight;
        }
      }

      if (!this.isConnected || this.invalidated) return;

      this.update();
      this.setBusy(false);

      this.hidden = false;
      this.fallback.hidden = true;
      this.ready = true;

      this.save();
    }

    async readCache() {
      try {
        const raw = sessionStorage.getItem(cacheKey);

        if (!raw || raw.length > 1500000) return;

        const entry = JSON.parse(raw);

        if (entry?.encoding !== 'gzip') return entry;
        if (typeof DecompressionStream !== 'function') return;

        const bytes = Uint8Array.from(
          atob(entry.data),
          character => character.charCodeAt(0)
        );

        const stream = new Blob([bytes]).stream().pipeThrough(
          new DecompressionStream('gzip')
        );

        const text = await new Response(stream).text();

        if (text.length > 8000000) return;

        return JSON.parse(text);
      } catch {
        return;
      }
    }

    prepare(root) {
      root.querySelectorAll('[reveal-on-scroll]').forEach(node => {
        node.removeAttribute('reveal-on-scroll');
      });
    }

    async fetchPage(page, signal) {
      const controller = new AbortController();
      const cancel = () => controller.abort();

      signal.addEventListener('abort', cancel, { once: true });

      const timer = setTimeout(cancel, 20000);

      try {
        if (signal.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }

        const url = this.pageUrl(page);

        url.searchParams.set(
          'section_id',
          this.dataset.sectionId
        );

        const response = await fetch(url, {
          signal: controller.signal,
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error('Collection request failed');
        }

        const doc = new DOMParser().parseFromString(
          await response.text(),
          'text/html'
        );

        const grid = doc.getElementById(this.grid.id);
        const controls = doc.querySelector('collection-load-more');
        const fallback = doc.querySelector(
          '[data-load-more-fallback]'
        );

        if (
          !grid ||
          !controls ||
          !fallback ||
          Number(controls.dataset.page) !== page
        ) {
          throw new Error('Invalid collection response');
        }

        return { grid, controls, fallback };
      } catch (error) {
        if (signal.aborted) throw error;

        throw new Error('Collection request failed');
      } finally {
        clearTimeout(timer);
        signal.removeEventListener('abort', cancel);
      }
    }

    applyPage(page) {
      for (const key of [
        'page',
        'pageCount',
        'total',
        'nextUrl',
        'progressTemplate'
      ]) {
        this.dataset[key] = page.controls.dataset[key];
      }

      this.fallback.replaceChildren(
        ...page.fallback.childNodes
      );
    }

    async loadNext(keyboard = false) {
      if (
        this.busy ||
        this.invalidated ||
        !this.dataset.nextUrl
      ) return;

      const page = Number(
        new URL(
          this.dataset.nextUrl,
          location.href
        ).searchParams.get('page')
      );

      if (page !== Number(this.dataset.page) + 1) return;

      this.setBusy(true);
      this.querySelector('[data-load-more-error]').hidden = true;

      this.request = new AbortController();

      try {
        const result = await this.fetchPage(
          page,
          this.request.signal
        );

        if (!this.isConnected || this.invalidated) return;

        this.prepare(result.grid);

        const firstCard = result.grid.querySelector('product-card');

        this.grid.append(...result.grid.childNodes);
        this.applyPage(result);

        this.state.lastPage = page;

        this.update();
        this.fallback.hidden = true;
        this.save();

        if (
          firstCard &&
          (keyboard || !this.dataset.nextUrl)
        ) {
          firstCard.tabIndex = -1;
          firstCard.focus({ preventScroll: true });

          firstCard.addEventListener('blur', () => {
            firstCard.removeAttribute('tabindex');
          }, { once: true });
        }
      } catch (error) {
        if (
          error.name !== 'AbortError' &&
          this.isConnected &&
          !this.invalidated
        ) {
          this.querySelector(
            '[data-load-more-error]'
          ).hidden = false;

          this.fallback.hidden = false;
        }
      } finally {
        if (this.isConnected) this.setBusy(false);
      }
    }

    update() {
      const count = this.cards.length;
      const action = this.querySelector('[data-load-more-action]');
      const link = action?.querySelector('a');

      if (action) {
        action.hidden = !this.dataset.nextUrl;
      }

      if (link && this.dataset.nextUrl) {
        link.href = this.dataset.nextUrl;
      }

      this.querySelector('[data-load-more-count]').textContent =
        this.dataset.progressTemplate.replace('__shown__', count);

      const progress = this.querySelector('progress-bar');

      progress.setAttribute('aria-valuenow', count);
      progress.setAttribute('aria-valuemax', this.dataset.total);
    }

    setBusy(value) {
      this.busy = value;
      this.setAttribute('aria-busy', String(value));

      const link = this.querySelector(
        '[data-load-more-action] a'
      );

      if (link) {
        link.setAttribute('aria-disabled', String(value));

        link.textContent = value ?
          this.dataset.loadingText :
          this.originalLabel;
      }
    }

    async save() {
      if (
        this.invalidated ||
        !this.isConnected ||
        !this.ready
      ) return;

      const anchor = this.cards.find(
        card => card.getBoundingClientRect().bottom > 0
      );

      const page = this.state.lastPage;
      let html;

      try {
        html = this.grid.innerHTML;
      } catch {}

      const cached = typeof html === 'string' &&
        this.cachedPage === page && this.cachedHTML === html;
      const pending = typeof html === 'string' &&
        this.cachePendingPage === page && this.cachePendingHTML === html;

      Object.assign(this.state, {
        scrollY: window.scrollY,
        gridHeight: Math.ceil(this.grid.getBoundingClientRect().height),
        viewportWidth: window.innerWidth,
        anchor: anchor ? {
          handle: anchor.getAttribute('handle'),
          top: anchor.getBoundingClientRect().top
        } : null
      });

      const url = this.pageUrl(page);

      try {
        history.replaceState({
          ...history.state,
          [stateKey]: this.state
        }, '', url);
      } catch {}

      if (cached || pending || typeof html !== 'string') return;

      const writeId = Symbol();
      const snapshotId = crypto.randomUUID();

      this.cacheWriteId = writeId;
      this.cachePendingPage = page;
      this.cachePendingHTML = html;

      try {
        await new Promise(resolve => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });

        if (
          !this.isConnected ||
          this.invalidated ||
          this.cacheWriteId !== writeId
        ) return;

        let text = JSON.stringify({
          id: this.state.id,
          snapshotId,
          lastPage: page,
          total: this.dataset.total,
          time: Date.now(),
          html,
          fallback: this.fallback.innerHTML,
          data: {
            page: this.dataset.page,
            pageCount: this.dataset.pageCount,
            total: this.dataset.total,
            nextUrl: this.dataset.nextUrl,
            progressTemplate: this.dataset.progressTemplate
          }
        });

        if (text.length > 8000000) {
          sessionStorage.removeItem(cacheKey);
          return;
        }

        if (
          typeof CompressionStream === 'function' &&
          typeof DecompressionStream === 'function'
        ) {
          const stream = new Blob([text]).stream().pipeThrough(
            new CompressionStream('gzip')
          );

          const bytes = new Uint8Array(
            await new Response(stream).arrayBuffer()
          );

          const chunks = [];

          for (let index = 0; index < bytes.length; index += 32768) {
            chunks.push(String.fromCharCode(
              ...bytes.subarray(index, index + 32768)
            ));
          }

          text = JSON.stringify({
            encoding: 'gzip',
            data: btoa(chunks.join(''))
          });
        }

        if (
          !this.isConnected ||
          this.invalidated ||
          this.cacheWriteId !== writeId
        ) return;

        if (text.length > 1500000) {
          sessionStorage.removeItem(cacheKey);
          return;
        }

        sessionStorage.setItem(cacheKey, text);
        this.cachedPage = page;
        this.cachedHTML = html;
        this.state.snapshotId = snapshotId;

        try {
          history.replaceState({
            ...history.state,
            [stateKey]: this.state
          }, '', this.pageUrl(page));
        } catch {}
      } catch {
        if (
          this.cacheWriteId === writeId &&
          this.isConnected &&
          !this.invalidated
        ) {
          try {
            sessionStorage.removeItem(cacheKey);
          } catch {}
        }
      } finally {
        if (this.cacheWriteId === writeId) {
          this.cachePendingPage = null;
          this.cachePendingHTML = null;
        }
      }
    }

    async restoreScroll(saved) {
      if (!Number.isFinite(saved.scrollY)) return;

      let interrupted = false;
      const stop = () => { interrupted = true; };

      const options = {
        once: true,
        passive: true,
        signal: this.life.signal
      };

      window.addEventListener('pointerdown', stop, options);
      window.addEventListener('wheel', stop, options);
      window.addEventListener('keydown', stop, options);

      await new Promise(resolve => {
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      });

      if (
        !this.isConnected ||
        this.invalidated ||
        interrupted
      ) return;

      const anchor = this.cards.find(
        card => card.getAttribute('handle') === saved.anchor?.handle
      );

      const top = anchor ?
        window.scrollY +
          anchor.getBoundingClientRect().top -
          saved.anchor.top :
        saved.scrollY;

      window.scrollTo({ top, behavior: 'instant' });
    }

    failRestore() {
      if (!this.isConnected || this.invalidated) return;

      this.setBusy(false);
      this.hidden = true;
      this.fallback.hidden = false;
    }
  }

  if (!customElements.get('collection-load-more')) {
    customElements.define(
      'collection-load-more',
      CollectionLoadMore
    );
  }
})();